import React, { useState, useEffect, type ReactNode } from 'react'
import { useQuery } from 'react-query'

import { useLogger, useTenantMappings } from '@vp/ubik-context'
import type { Logger } from '@vp/ubik-logging'

import { useAppState } from '../../../root/contexts/AppContext'
import { useWorksDataDispatch } from '../../../root/contexts/WorksDataContext'
import type { HardGoodAvailability, SizeAvailability } from '../../../../types/Inventory'
import { useProject } from '../ProjectContext'
import type { MerchandisingInventory } from '../../../../types/MerchantAvailability'
import type { WorkProduct } from '../../../../types/Work'
import { updateProduct } from '../WorksDataContext/WorkDataAction'
import { getMerchantAvailability } from '../../../../clients/MerchantAvailabilityClient'
import { useAuthorization } from '../AuthorizationProvider'
import useCrossSizeMoq from '../../../../hooks/queries/useCrossSizeMoq'

type Inventory = SizeAvailability[] | HardGoodAvailability

export interface IInventoryContext {
  inventory: Inventory
  isInventoryStateInitialized: boolean
}

export const InventoryContext = React.createContext({} as IInventoryContext)

const INFINITE_INVENTORY = 999999

function useMerchantAvailability(
  merchant: string,
  productKey: string,
  productVersion: number,
  country: string,
  crossSizeMoq: number,
  selectedAttributes: Record<string, string>,
  isSizedGood: boolean,
  authorizationHeader: string | undefined,
  ownerAccountId: string,
  logger: Logger,
  isCrossSizeMoqLoading: boolean,
) {
  return useQuery<MerchandisingInventory | undefined>(
    ['merchantAvailability', merchant, productKey, productVersion, country, crossSizeMoq, selectedAttributes, isSizedGood],
    async () => {
      const inventory = await getMerchantAvailability(
        merchant,
        productKey,
        productVersion,
        country,
        crossSizeMoq,
        selectedAttributes,
        isSizedGood,
        authorizationHeader!,
        ownerAccountId,
        logger,
      )
      return inventory
    },
    {
      // Wait for the cross-size MOQ to resolve first so the low-stock threshold is computed
      // with its final value instead of the fallback, avoiding a redundant refetch.
      enabled: !!productKey && !!productVersion && !!country && !!authorizationHeader && !!ownerAccountId && !isCrossSizeMoqLoading,
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  )
}

const InventoryContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [inventory, setInventory] = useState<Inventory>({} as Inventory)
  const [isInventoryStateInitialized, setIsInventoryStateInitialized] = useState<boolean>(false)

  const { country } = useAppState()
  const { workId, productKey, productVersion, mpvId, sizes, isSizedGood, lowestPriceAttributes } = useProject()
  const worksDataDispatch = useWorksDataDispatch()
  const { authorizationHeader } = useAuthorization()
  const { pim: merchantId, account: ownerAccountId } = useTenantMappings()
  const logger = useLogger()
  const { crossSizeMoq, isLoading: isCrossSizeMoqLoading } = useCrossSizeMoq()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { Size, ...selectedAttributes } = lowestPriceAttributes
  const { data: productInventory, isLoading: inventoryIsLoading } = useMerchantAvailability(
    merchantId,
    productKey,
    productVersion,
    country,
    crossSizeMoq,
    selectedAttributes,
    isSizedGood,
    authorizationHeader,
    ownerAccountId,
    logger,
    isCrossSizeMoqLoading,
  )

  const initializeInventoryState = async () => {
    try {
      if (!productKey || !productVersion || !mpvId) {
        logger.error('Unable to initialize inventory state', { workId, productKey, productVersion, mpvId, sizes: JSON.stringify(sizes) })
        return
      }

      let enabledInventory = [] as Inventory

      const productInventoryCombinations = productInventory?.combinationsInventories

      let hasNoInventoryInfo = false
      if (!productInventory) {
        logger.warn('No inventory info available for current selected options')
        hasNoInventoryInfo = true
      }

      if (isSizedGood) {
        const availableSizes = sizes?.values
        availableSizes?.forEach((size) => {
          const sizeAvailability: SizeAvailability = {
            size: { key: size.key, name: size.name },
            numAvailable: INFINITE_INVENTORY,
            lowStock: false,
          }

          const singleSizeInventory = productInventoryCombinations?.find(
            (inventory) => inventory.attributes.find((attr) => attr.name === 'Size')?.value === size.key,
          )

          if (singleSizeInventory) {
            sizeAvailability.numAvailable = singleSizeInventory.inventory
            sizeAvailability.lowStock = singleSizeInventory.inventoryLevel === 'Low'
          }

          if (singleSizeInventory || hasNoInventoryInfo) {
            ;(enabledInventory as SizeAvailability[]).push(sizeAvailability)
          }
        })
      } else {
        // Get inventory for hard good
        enabledInventory = {
          numAvailable: productInventory?.inventory ?? INFINITE_INVENTORY,
          lowStock: productInventory?.inventoryLevel === 'Low' || false,
        }
      }

      const payload: WorkProduct = {
        productInfo: {
          productKey,
          productVersion,
          selections: lowestPriceAttributes,
        },
      }

      setInventory(enabledInventory)
      setIsInventoryStateInitialized(true)
      worksDataDispatch(updateProduct(workId, payload))
    } catch (error) {
      //TODO: implement error handling
      logger.error('Failed to initialize inventory state', error as Error, { workId, productKey, productVersion, mpvId, sizes })
    }
  }

  useEffect(() => {
    if (inventoryIsLoading || isCrossSizeMoqLoading) {
      return
    }
    initializeInventoryState()
  }, [inventoryIsLoading, isCrossSizeMoqLoading])

  return (
    <>
      <InventoryContext.Provider
        value={{
          inventory,
          isInventoryStateInitialized,
        }}
      >
        {children}
      </InventoryContext.Provider>
    </>
  )
}

export default InventoryContextProvider
